import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { concatMap, from, map, Observable, of, tap, toArray } from 'rxjs';
import {
  formatLocalDateTime,
  getBulkFrequencyDates,
  MAX_BULK_FREQUENCY_OCCURRENCES,
} from './utils';

export const ALREADY_SAVED_BULK_FREQUENCY_MESSAGE =
  'These frequency entries were already saved. Change the time range or occurrence count before saving again.';

export function bulkFrequencyOccurrencesValidators(): ValidatorFn[] {
  return [
    Validators.required,
    Validators.min(1),
    Validators.max(MAX_BULK_FREQUENCY_OCCURRENCES),
    Validators.pattern(/^[1-9]\d*$/),
  ];
}

export function showBulkFrequencyEndDate(
  isFrequency: boolean,
  occurrences: number | null | undefined
): boolean {
  return isFrequency && (occurrences ?? 1) > 1;
}

export function updateBulkFrequencyEndDateValidators(
  isFrequency: boolean,
  occurrences: number | null | undefined,
  endDate: FormControl<string | null>
): void {
  if (showBulkFrequencyEndDate(isFrequency, occurrences)) {
    endDate.setValidators([Validators.required]);
  } else {
    endDate.clearValidators();
  }
  endDate.updateValueAndValidity({ emitEvent: false });
}

export function bulkFrequencyRangeError(
  isFrequency: boolean,
  occurrences: number | null | undefined,
  endDate: FormControl<string | null>
): string | null {
  updateBulkFrequencyEndDateValidators(isFrequency, occurrences, endDate);
  if (showBulkFrequencyEndDate(isFrequency, occurrences) && endDate.invalid) {
    return 'Please enter start, end, and a valid whole number of occurrences.';
  }
  return null;
}

export function resolveBulkFrequencyRecordedDates(
  isFrequency: boolean,
  startValue: string,
  endValue: string | null | undefined,
  occurrences: number | null | undefined
): string[] {
  if (!isFrequency) {
    return [startValue];
  }

  const count = occurrences ?? 1;
  if (!Number.isInteger(count) || count < 1) {
    throw new Error('Please enter a valid whole number of occurrences.');
  }
  if (count > MAX_BULK_FREQUENCY_OCCURRENCES) {
    throw new Error(`Occurrences cannot exceed ${MAX_BULK_FREQUENCY_OCCURRENCES}.`);
  }
  if (count === 1) {
    return [startValue];
  }

  return getBulkFrequencyDates(startValue, endValue ?? '', count).map(formatLocalDateTime);
}

export function bulkFrequencySaveKey(
  kind: 'behavior' | 'target',
  sessionId: number | null | undefined,
  itemId: number | null | undefined,
  recordedDate: string | null | undefined
): string {
  return `${kind}|${sessionId ?? 0}|${itemId ?? 0}|${recordedDate ?? ''}`;
}

export function saveBulkFrequencyEntries<T>(
  entries: T[],
  savedKeys: Set<string>,
  keyOf: (entry: T) => string,
  save: (entry: T) => Observable<unknown>
): Observable<{ skippedAll: boolean; requestedCount: number; entriesToSaveCount: number }> {
  const entriesToSave = entries.filter((entry) => !savedKeys.has(keyOf(entry)));
  if (entriesToSave.length === 0) {
    return of({
      skippedAll: true,
      requestedCount: entries.length,
      entriesToSaveCount: 0,
    });
  }

  return from(entriesToSave).pipe(
    concatMap((entry) =>
      save(entry).pipe(tap(() => savedKeys.add(keyOf(entry))))
    ),
    toArray(),
    map(() => ({
      skippedAll: false,
      requestedCount: entries.length,
      entriesToSaveCount: entriesToSave.length,
    }))
  );
}

export function bulkFrequencySuccessMessage(
  noun: 'behavior' | 'target',
  count: number
): string {
  return count === 1
    ? `${noun === 'behavior' ? 'Behavior' : 'Target'} data saved successfully`
    : `${count} ${noun} entries saved successfully`;
}

export function bulkFrequencyErrorMessage(
  noun: 'behavior' | 'target',
  ajaxError: string,
  entriesToSaveCount: number,
  savedCount: number,
  requestedCount: number
): string {
  if (entriesToSaveCount === 1) {
    return `Error saving ${noun} data - ${ajaxError}`;
  }
  return `${savedCount} of ${requestedCount} ${noun} entries were saved before an error occurred - ${ajaxError}`;
}
